import { IAddress } from "./businessFull.interface";

export interface IOnboarding {
  _id: string;
  createdAt: string;
  status: string;
  email: string;
  partnerRefCode: string;
  externalMerchantId: string;
  externalLink: string;
  provider: "TransactionLink";
  acquirerStatus: TLAcquirerStatus;
  pendingWorkflows: FormattedTLPendingWorkflow[];
  externalMerchant: TransactionLinkMerchantData;
}

export interface TransactionLinkMerchantData {
  id: string;
  name: string;
  version: number;
  status: object;
  country: string;
  coNum: string;
  email: string;
  url: string;
  address: ITransactionLinkAddress[];
  overlyingCompanies: ITransactionLinkOverlyingCompany[];
  personalInfo: PersonalInfo[];
  idv: IDVInfo[];
  workflowAlerts: WorkflowAlert[];
  shareholdersReg: Shareholder[];
  productInfo: ProductInfo[];
  bankingInfo: BankingInfo[];
  representativesUser: Representative[];
  repsRegistry: RepsRegistry[];
  uboCalc: UboCalc[];
  ubosUser: Ubo[];
  entityType: ValitorBusinessType;
  incorporationDate?: string;
  acquirerStatus?: { color: string; value: TLAcquirerStatus };
  submissionErrorMessage?: string;
  submissionErrorType?: TLSubmissionErrorType;
  subflow?: TLPendingWorkflow[];
}

interface ProductInfo {
  createdAt: string;
  id: string;
  mcc: string;
  merchants: any[];
  version: number;
  description: string;
  avgMonthlyTrxValue: number;
  avgOrderValue: number;
  updatedAt: string;
  name: string;
}

interface PersonalInfo {
  id: string;
  dob: string;
  firstName: string;
  createdAt: string | null;
  updatedAt: string | null;
  version: number;
  lastName: string;
  authorised: boolean;
  address: ITransactionLinkAddress;
  merchants: any[];
  name: string;
  phoneCountryCode: string;
  phone: string;
}

interface BankingInfo {
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
  version: number;
  iban: string;
  payoutSchedule: "daily" | "weekly" | "monthly";
  merchants: any[];
  name: string;
}

interface IDVInfo {
  lastName: string;
  validTo: string;
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
  version: number;
  nationalId: string;
  firstName: string;
  documentType: string;
  merchants: any[];
  status: object;
  name: string;
}

interface Shareholder {
  createdAt: string;
  percentage: number;
  name: string;
  id: string;
  shareholderType: string;
  merchants: any[];
  version: number;
  updatedAt: string;
}

interface Representative {
  doa: string;
  dob: string;
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
  name: string;
  version: number;
  firstName: string;
  lastName: string;
  position: { color: string; value: ValitorBusinessRole }[];
  isAuthorized?: boolean;
  merchants: any[];
  address?: ITransactionLinkAddress[];
  country?: string;
  phone?: string;
  phoneCountryCode?: string;
}

export type ValitorBusinessRole =
  | "PrimaryContact"
  | "Director"
  | "AuthorizedSignatory"
  | "BoardOfDirectors"
  | "BeneficialOwner"
  | "VotingControl"
  | "PersonShareOwner";

interface Ubo {
  dob: string;
  id: string;
  country: string;
  createdAt: string | null;
  updatedAt: string | null;
  controlBasedUbo: boolean;
  ownershipBasedUbo: boolean;
  percentage: number;
  name: string;
  version: number;
  firstName: string;
  lastName: string;
  merchants: any[];
}
interface RepsRegistry {
  createdAt: "2025-04-16T13:47:59.407189Z";
  name: "LemonPay Ltd.";
  id: "358a50fe-4508-4e09-bfcf-48887f9802ff";
  position: "PersonShareOwner";
  version: 1;
  updatedAt: "2025-04-16T13:47:59.42847Z";
}

interface WorkflowAlert {
  ibaNmismatch2: boolean;
  ibaNmismatch1: boolean;
  uboInvestigationIsNeeded: boolean;
  version: number;
  au10tixResponseFlagAMissmatch: boolean;
  createdAt: string;
  id: string;
  au10tixIdentityMismatch2: boolean;
  updatedAt: string;
}
interface ITransactionLinkOverlyingCompany {
  dateSince: string;
  registration: string;
  createdAt: string | null;
  updatedAt: string | null;
  name: string;
  version: number;
  position: string;
}

interface UboCalc {
  percentage: number | null;
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
  name: string;
  version: number;
  merchants: any[];
}
interface ITransactionLinkAddress extends IAddress {
  id: string;
  version: string;
  updatedAt: string;
  createdAt: string;
}

/** Mirrors TLAcquirerStatus in the backend's shared/types/transactionLinkMerchantData.ts. */
export enum TLAcquirerStatus {
  NotReady = "Not ready",
  PendingSubmission = "Pending submission",
  SubmissionError = "Submission error",
  PendingVerification = "Pending verification",
  Approved = "Approved",
  Rejected = "Rejected",
}
export enum TLSubmissionErrorType {
  Acquirer = "acquirer",
  Processor = "processor",
  InternalServer = "internalServer",
}

export enum TLPendingWorkflowStatus {
  InQualification = "In qualification",
  Ready = "Ready To Submit",
  Failed = "Failed",
  Submitted = "Submitted",
}
export enum TLPendingWorkflowUserStatus {
  Started = "User started subflow",
  WaitingOnEmailClick = "Waiting for user to click email link",
  Completed = "User completed subflow",
}
export enum TLPendingWorkflowName {
  Au10tix = "AU10TIX",
  UBO = "UBO",
  GENERAL = "GENERAL",
}
export interface TLPendingWorkflow {
  status: { color: string; value: TLPendingWorkflowStatus };
  userStatus: { color: string; value: TLPendingWorkflowUserStatus };
  name: { color: string; value: TLPendingWorkflowName };
  email: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface FormattedTLPendingWorkflow {
  status: TLPendingWorkflowStatus;
  userStatus: TLPendingWorkflowUserStatus;
  name: TLPendingWorkflowName;
  workflowId: string;
  email: string;
  link?: string;
}

export type ValitorBusinessType =
  | "soleTrader"
  | "publicLimitedCompany"
  | "privateLimitedCompany"
  | "partnership"
  | "other";
